import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand, useProductName } from "@/context/BrandContext";
import api, { formatApiError } from "@/lib/api";
import { Phone } from "@phosphor-icons/react";

export default function VerifyEmail() {
  const { brand } = useBrand();
  const productName = useProductName();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setError("Missing verification token.");
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        if (cancelled) return;
        if (data?.access_token) {
          localStorage.setItem("wa_token", data.access_token);
          await refresh?.();
          navigate("/dashboard", { replace: true });
          return;
        }
        navigate("/login", { replace: true });
      } catch (err) {
        if (!cancelled) {
          setError(formatApiError(err.response?.data?.detail, err) || err.message);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate, refresh]);

  const logo = brand.logo_url || brand.login_logo_url;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="verify-email-page">
      <div className="w-full max-w-sm text-center">
        <div className="flex items-center justify-center gap-3 mb-8">
          {logo ? (
            <img src={logo} alt="" className="h-10 w-10 object-contain" />
          ) : (
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary text-primary-foreground">
              <Phone className="h-5 w-5" weight="fill" />
            </div>
          )}
          <p className="font-display font-bold tracking-tight">{productName || "Account"}</p>
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground" data-testid="verify-email-loading">Activating your account…</p>
        ) : (
          <div data-testid="verify-email-error">
            <h1 className="text-2xl font-display font-semibold tracking-tight">Activation failed</h1>
            <p className="text-sm text-destructive mt-3">{error}</p>
            <p className="text-sm text-muted-foreground mt-3">
              Ask your administrator to activate the account, or sign up again.
            </p>
            <Link to="/login" className="mt-8 inline-flex h-11 w-full items-center justify-center rounded-lg bg-primary text-sm font-medium text-primary-foreground">
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
